import React, { useState } from 'react';
import { Alert, Button, Spin, Switch, Typography } from 'antd';
// Hooks
import { useGetCbSettings, useSetCbSetting } from 'hooks';
// Types
import {
  GetCbSettings,
  GetCbSettings_getCbSettings_CbAgencySignUpEmailVerificationSetting,
} from 'api/cbSettings/types/GetCbSettings';
import { CbSettingScope, CbSettingType } from 'api/graphql-global-types';
// UI
import { successNotification, errorNotification } from 'ui/Notification';
// Styles
import styles from './AgencySignUpEmailVerificationSettings.module.scss';

const { Text } = Typography;

type AgencySignUpEmailVerificationSetting =
  GetCbSettings_getCbSettings_CbAgencySignUpEmailVerificationSetting;

const isAgencySignUpEmailVerificationSetting = (
  setting: GetCbSettings['getCbSettings'][number]
): setting is AgencySignUpEmailVerificationSetting =>
  setting.__typename === 'CbAgencySignUpEmailVerificationSetting';

const AgencySignUpEmailVerificationSettings = (): JSX.Element => {
  const [draft, setDraft] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);

  const { data, loading, error, refetch } = useGetCbSettings({
    scope: CbSettingScope.Global,
    type: CbSettingType.AgencySignUpEmailVerification,
  });
  const [setCbSetting] = useSetCbSetting();

  const savedValue =
    (data?.getCbSettings ?? []).find(isAgencySignUpEmailVerificationSetting)
      ?.enabled ?? false;
  const value = draft ?? savedValue;
  const changed = draft !== null && draft !== savedValue;

  const saveSetting = async (): Promise<boolean> => {
    try {
      await setCbSetting({
        variables: {
          input: {
            type: CbSettingType.AgencySignUpEmailVerification,
            agencySignUpEmailVerification: { enabled: value },
          },
        },
      });
      successNotification('Sign up settings saved');
      return true;
    } catch (err: any) {
      errorNotification(err?.message || 'Failed to save the sign up settings');
      return false;
    }
  };

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    if (await saveSetting()) {
      await refetch()
        .then(() => setDraft(null))
        .catch(() => undefined);
    }
    setSaving(false);
  };

  if (loading && !data) {
    return (
      <div className={styles.loading}>
        <Spin />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="Failed to load the sign up settings"
        description={error.message}
      />
    );
  }

  return (
    <div className={styles.root}>
      <Text type="secondary">
        When on, brands signing up with an email and password have to confirm
        their email with a code before the account is created. Google sign up is
        not affected.
      </Text>

      <div className={styles.row}>
        <Text strong>Email verification</Text>
        <Switch
          checked={value}
          disabled={saving}
          onChange={(checked) => setDraft(checked)}
        />
      </div>

      <div className={styles.actions}>
        <Button
          type="primary"
          loading={saving}
          disabled={!changed}
          onClick={handleSave}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default AgencySignUpEmailVerificationSettings;
